'use client';

import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { toasterApi } from '../../toast/ToastProvider';
import { SettingsDangerSection } from '../../settings/SettingsDangerSection';

export type InternalNetworkDangerContentProps = {
  networkName: string;
  isDiscoveryActive: boolean;
  isResetting: boolean;
  isDeleting: boolean;
  onResetDiscoveryData: () => Promise<void>;
  onDelete: () => Promise<void>;
  isResetConflictError: (error: unknown) => boolean;
};

export function InternalNetworkDangerContent({
  networkName,
  isDiscoveryActive,
  isResetting,
  isDeleting,
  onResetDiscoveryData,
  onDelete,
  isResetConflictError,
}: InternalNetworkDangerContentProps) {
  const t = useTranslations('designSystem.networkSettings.internalDanger');
  const tCommon = useTranslations('designSystem.common');

  const handleResetDiscoveryData = async () => {
    try {
      await onResetDiscoveryData();
      toasterApi.success({ title: t('resetDiscoveryDataSuccess') });
    } catch (error: unknown) {
      console.error(
        new Error('Failed to reset internal network discovery data'),
        error,
      );
      toasterApi.error({
        title: isResetConflictError(error)
          ? t('resetDiscoveryDataConflict')
          : t('resetDiscoveryDataError'),
      });
    }
  };

  const handleDelete = async () => {
    try {
      await onDelete();
      toasterApi.success({ title: t('deleteSuccess') });
    } catch (error: unknown) {
      console.error(new Error('Failed to delete internal network'), error);
      toasterApi.error({ title: t('deleteError') });
    }
  };

  return (
    <VStack alignItems="stretch" gap={10} w="full">
      <SettingsDangerSection
        rowLabel={t('resetDiscoveryData')}
        actionLabel={t('resetDiscoveryData')}
        warningTitle={t('resetDiscoveryDataWarningTitle')}
        warningDescription={t('resetDiscoveryDataWarningDescription')}
        confirmTitle={t('resetDiscoveryDataConfirmTitle')}
        confirmDescription={t('resetDiscoveryDataConfirmDescription', {
          name: networkName,
        })}
        confirmText={t('resetDiscoveryDataConfirmText')}
        cancelText={tCommon('cancel')}
        onAction={handleResetDiscoveryData}
        loading={isResetting}
        actionDisabled={isDiscoveryActive}
        severity="neutral"
      />
      <SettingsDangerSection
        rowLabel={t('deleteNetwork')}
        actionLabel={t('deleteNetwork')}
        warningTitle={t('warningTitle')}
        warningDescription={t('description')}
        confirmTitle={t('confirmTitle')}
        confirmDescription={t('confirmDescription', { name: networkName })}
        confirmText={t('confirmText')}
        cancelText={tCommon('cancel')}
        onAction={handleDelete}
        loading={isDeleting}
      />
    </VStack>
  );
}
